import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Typography from '../StyledTypography';
import StyledTabs from '../StyledTabs/StyledTabs';
import SubformNavigation from '../SubformNavigation/SubformNavigation';
import FormCrafterFormRenderer from '../FormCrafterFormRenderer/FormCrafterFormRenderer';
import FormCrafterListView from '../FormCrafterFormRenderer/FormCrafterListView';
import styles from './MultiFormView.module.css';

/**
 * MultiFormView - Common component for displaying multiple forms with tabs and subform navigation
 *
 * Provides a consistent UI structure for:
 * - New request submission (editable)
 * - Send-back editing (editable)
 * - Request progress viewing (read-only)
 *
 * @param {Object} props
 * @param {Array} props.forms - Array of form objects with id, name, subforms
 * @param {Object} props.formData - Form data keyed by form ID
 * @param {Object} props.comments - Comments keyed by form ID
 * @param {Function} props.onCommentsUpdate - Callback when comments change
 * @param {number} props.activeFormIndex - Active form tab index
 * @param {number} props.activeSubformIndex - Active subform index
 * @param {Function} props.onFormTabChange - Callback for form tab change
 * @param {Function} props.onSubformNavigate - Callback for subform navigation
 * @param {boolean} props.disabled - Whether forms are read-only
 * @param {boolean} props.useListView - Whether to use list view (key-value) instead of form renderer
 * @param {Object} props.formRefsMap - Ref map for form validation (optional)
 * @param {Array} props.fileMetadata - File metadata array (optional)
 * @param {React.ReactNode} props.requestAuditTrailsSlot - Optional slot for Request Audit Trails tab content
 * @param {React.ReactNode} props.additionalDocumentsSlot - Optional slot for Additional Documents tab content
 * @param {React.ReactNode} props.requestClarificationWavesSlot - Optional slot for Request Clarification Waves tab content
 * @param {React.ReactNode} props.linkedStudiesSelectorSlot - Optional slot for Linked Studies selector tab content (for new requests)
 * @param {React.ReactNode} props.linkedStudiesViewSlot - Optional slot for Linked Studies view tab content (for existing requests)
 * @param {boolean} props.showValidationIndicators - Whether to show validation indicators (default: true)
 * @param {Function} props.onValidationChange - Callback when validation state changes per form/subform
 * @param {Function} props.getFormApprovalStatus - Function to get approval status for a form's filled form ID
 * @param {Function} props.getFormCommentStatus - Function to get comment status for a form ID
 * @param {boolean} props.enableAddComment - Whether to enable adding comments (for send-back scenarios)
 * @param {React.ReactNode} props.topSlot - Optional slot for content at top of form (e.g., badges)
 * @param {boolean} props.showRequiredIndicator - Whether to show required asterisk on required fields (for applicants)
 * @param {number|string} props.requestId - Request ID for field waves feature and send-back/edit
 * @param {boolean} props.enableFieldWaves - Whether to enable field waves viewing
 */
const MultiFormView = ({
  forms = [],
  formData = {},
  comments = {},
  onCommentsUpdate,
  activeFormIndex = 0,
  activeSubformIndex = 0,
  onFormTabChange,
  onSubformNavigate,
  disabled = false,
  useListView = false,
  formRefsMap,
  fileMetadata = [],
  requestAuditTrailsSlot,
  additionalDocumentsSlot,
  requestClarificationWavesSlot,
  linkedStudiesSelectorSlot,
  linkedStudiesViewSlot,
  showValidationIndicators = true,
  onValidationChange,
  getFormApprovalStatus,
  getFormCommentStatus,
  enableAddComment = false,
  enableFieldEdit = false,
  markPendingEdit,
  markResolvedEdit,
  topSlot,
  showRequiredIndicator = false,
  requestId,
  enableFieldWaves = false,
  // Props for eager file upload
  onStagedFileUpload,
  onStagedFileDelete,
  eagerUploadEnabled = false,
  // Props for draft staged files (pre-loaded from draft)
  draftStagedFiles = {},
  // Props for deleted file IDs in draft (sandbox behavior)
  draftDeletedFileIds = [],
  onServerFileDeleteConfirmed,
  // Comment edit/delete props
  requestProgress,
  requestDateEnded,
  currentUserEmail,
  onCommentUpdated,
  onCommentDeleted,
  onSubformCommentAdded,
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const hasRequestAuditTrailsTab = !!requestAuditTrailsSlot;
  const hasAdditionalDocumentsTab = !!additionalDocumentsSlot;
  const hasRequestClarificationWavesTab = !!requestClarificationWavesSlot;
  const hasLinkedStudiesSelectorTab = !!linkedStudiesSelectorSlot;
  const hasLinkedStudiesViewTab = !!linkedStudiesViewSlot;

  // Calculate dynamic tab indices based on which optional tabs exist
  let nextTabIndex = forms.length;
  const tabs = useMemo(
    () => ({
      linkedStudiesSelectorIndex: hasLinkedStudiesSelectorTab
        ? nextTabIndex++
        : -1,
      linkedStudiesViewIndex: hasLinkedStudiesViewTab ? nextTabIndex++ : -1,
      additionalDocumentsIndex: hasAdditionalDocumentsTab ? nextTabIndex++ : -1,
      requestAuditTrailsIndex: hasRequestAuditTrailsTab ? nextTabIndex++ : -1,
      requestClarificationWavesIndex: hasRequestClarificationWavesTab
        ? nextTabIndex++
        : -1,
    }),
    [
      hasLinkedStudiesSelectorTab,
      nextTabIndex,
      hasLinkedStudiesViewTab,
      hasAdditionalDocumentsTab,
      hasRequestAuditTrailsTab,
      hasRequestClarificationWavesTab,
    ]
  );
  const isFormTab = activeFormIndex < forms.length;

  // Track validation state per form/subform
  const [validationState, setValidationState] = useState({});

  // Handle validation change from FormCrafterFormRenderer
  const handleValidationChange = useCallback(
    (formId, subformId, validationInfo) => {
      setValidationState((prev) => ({
        ...prev,
        [formId]: {
          ...prev[formId],
          [subformId]: validationInfo,
        },
      }));

      // Notify parent if callback provided
      if (onValidationChange) {
        onValidationChange(formId, subformId, validationInfo);
      }
    },
    [onValidationChange]
  );

  // Get validation errors for a specific form's subforms
  const getValidationErrorsForForm = useCallback(
    (formId) => {
      return validationState[formId] || {};
    },
    [validationState]
  );

  // Check if a form has any validation errors
  const formHasErrors = useCallback(
    (formId) => {
      const formErrors = validationState[formId];
      if (!formErrors) return false;
      return Object.values(formErrors).some(
        (subformError) => subformError?.hasError
      );
    },
    [validationState]
  );

  // Show tabs when there's more than one form or audit trails slot
  const shouldShowTabs = forms.length > 1 || hasRequestAuditTrailsTab;
  const firstForm = forms[0];

  // Get file metadata for a specific form
  const getFileMetadataForForm = useMemo(() => {
    return (formId) => {
      const filled = formData[formId];
      if (filled?.fileMetadata) {
        return filled.fileMetadata;
      }
      // Fallback to global fileMetadata if available
      return fileMetadata;
    };
  }, [formData, fileMetadata]);

  // Helper to compute comment status from comments array
  const computeCommentStatus = useCallback((formComments) => {
    const status = {};
    if (formComments && Array.isArray(formComments)) {
      formComments.forEach((comment) => {
        if (comment.subForm_id) {
          status[comment.subForm_id] = true;
        }
      });
    }
    return status;
  }, []);

  const multiFormTabConfigs = useMemo(() => {
    return [
      ...forms.map((form, index) => {
        const hasErrors = showValidationIndicators && formHasErrors(form.id);
        const tabLabel = form.nameUnreliable
          ? `${t('multiFormSubmission.form')} ${index + 1}`
          : form.name || `${t('multiFormSubmission.form')} ${index + 1}`;
        return {
          key: form.id || index,
          label: tabLabel,
          hasError: hasErrors,
        };
      }),
      ...(hasLinkedStudiesSelectorTab
        ? [{ key: 'linkedStudiesSelector', label: t('linkedStudies') }]
        : []),
      ...(hasLinkedStudiesViewTab
        ? [{ key: 'linkedStudiesView', label: t('linkedStudies') }]
        : []),
      ...(hasAdditionalDocumentsTab
        ? [{ key: 'additionalDocuments', label: t('additionalDocuments') }]
        : []),
      ...(hasRequestAuditTrailsTab
        ? [{ key: 'auditTrails', label: t('auditTrails') }]
        : []),
      ...(hasRequestClarificationWavesTab
        ? [
            {
              key: 'clarificationWaves',
              label: t('clarificationWaves.tabLabel'),
            },
          ]
        : []),
    ];
  }, [
    forms,
    t,
    showValidationIndicators,
    formHasErrors,
    hasLinkedStudiesSelectorTab,
    hasLinkedStudiesViewTab,
    hasAdditionalDocumentsTab,
    hasRequestAuditTrailsTab,
    hasRequestClarificationWavesTab,
  ]);

  useEffect(() => {
    if (hasRequestClarificationWavesTab) {
      const tabParam = searchParams.get('tab');
      if (
        tabParam === 'clarifications' &&
        tabs.requestClarificationWavesIndex !== -1
      ) {
        onFormTabChange(null, tabs.requestClarificationWavesIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRequestClarificationWavesTab, tabs]);

  if (forms.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      {shouldShowTabs ? (
        <StyledTabs
          value={activeFormIndex}
          onChange={onFormTabChange}
          variant={forms.length > 4 ? 'scrollable' : undefined}
          tabs={multiFormTabConfigs}
        />
      ) : (
        firstForm?.name && (
          <Typography variant="title2" className={styles.singleFormTitle}>
            {firstForm.name}
          </Typography>
        )
      )}

      {/* Render ALL forms but only display active one - this keeps refs alive for validation */}
      {forms.map((form, index) => {
        const isActive = isFormTab && index === activeFormIndex;
        const formSubforms = form.subforms || [];
        const formComments = comments[form.id] || [];
        // Use exact match by form ID only - no fallback to prevent cross-stage data leakage
        // For first-time submissions, this will be null (empty form)
        // For send-backs, the backend returns the correct data keyed by form ID
        const filledForm = formData[form.id] || null;
        const formFileMetadata = getFileMetadataForForm(form.id);
        const formValidationErrors = getValidationErrorsForForm(form.id);

        // Get approval and comment status for this form
        const approvalStatus =
          isActive && getFormApprovalStatus
            ? getFormApprovalStatus(form.id)
            : {};
        const commentStatus = getFormCommentStatus
          ? getFormCommentStatus(form.id)
          : computeCommentStatus(formComments);

        return (
          <div
            key={`form-content-${form.id}`}
            className={`${styles.formContent}${!isActive ? ` ${styles.formContentHidden}` : ''}`}
          >
            <SubformNavigation
              items={formSubforms}
              activeIndex={isActive ? activeSubformIndex : 0}
              onNavigate={isActive ? onSubformNavigate : () => {}}
              validationErrors={formValidationErrors}
              showValidationIndicators={showValidationIndicators && !disabled}
              approvalStatus={approvalStatus}
              commentStatus={commentStatus}
              headerSlot={isActive ? topSlot : null}
            >
              {useListView ? (
                <>
                  <FormCrafterListView
                    subforms={formSubforms}
                    filledForm={filledForm}
                    fileMetadata={formFileMetadata}
                    comments={formComments}
                    setComments={
                      onCommentsUpdate
                        ? (updater) => onCommentsUpdate(form.id, updater)
                        : undefined
                    }
                    enableAddComment={enableAddComment}
                    activeSubformIndex={isActive ? activeSubformIndex : null}
                    formId={form.id}
                    enableFieldEdit={enableFieldEdit && !disabled}
                    formRendererRef={formRefsMap?.current?.[form.id]}
                    markPendingEdit={markPendingEdit}
                    markResolvedEdit={markResolvedEdit}
                    showRequiredIndicator={showRequiredIndicator}
                    requestId={requestId}
                    enableFieldWaves={enableFieldWaves}
                    onStagedFileUpload={onStagedFileUpload}
                    onStagedFileDelete={onStagedFileDelete}
                    eagerUploadEnabled={eagerUploadEnabled}
                    stagedFilesByField={draftStagedFiles}
                    draftDeletedFileIds={draftDeletedFileIds}
                    onServerFileDeleteConfirmed={onServerFileDeleteConfirmed}
                    requestProgress={requestProgress}
                    requestDateEnded={requestDateEnded}
                    currentUserEmail={currentUserEmail}
                    onCommentUpdated={onCommentUpdated}
                    onCommentDeleted={onCommentDeleted}
                    onSubformCommentAdded={onSubformCommentAdded}
                  />

                  {/* Keep FormCrafter mounted for validation + submission when editable but using list view */}
                  {!disabled && (
                    <div className={styles.formContentHidden}>
                      <FormCrafterFormRenderer
                        ref={formRefsMap?.current?.[form.id]}
                        formId={form.id}
                        subforms={formSubforms}
                        filledForm={filledForm}
                        comments={formComments}
                        setComments={
                          onCommentsUpdate
                            ? (updater) => onCommentsUpdate(form.id, updater)
                            : undefined
                        }
                        disabled={false}
                        enableAddComment={enableAddComment}
                        showSubmitButton={false}
                        activeSubformIndex={
                          isActive ? activeSubformIndex : null
                        }
                        onValidationChange={(subformId, validationInfo) =>
                          handleValidationChange(
                            form.id,
                            subformId,
                            validationInfo
                          )
                        }
                        validateOnBlur={true}
                        onStagedFileUpload={onStagedFileUpload}
                        onStagedFileDelete={onStagedFileDelete}
                        eagerUploadEnabled={eagerUploadEnabled}
                        draftStagedFiles={draftStagedFiles}
                        draftDeletedFileIds={draftDeletedFileIds}
                        onServerFileDeleteConfirmed={
                          onServerFileDeleteConfirmed
                        }
                      />
                    </div>
                  )}
                </>
              ) : (
                <FormCrafterFormRenderer
                  ref={formRefsMap?.current?.[form.id]}
                  formId={form.id}
                  subforms={formSubforms}
                  filledForm={filledForm}
                  comments={formComments}
                  setComments={
                    onCommentsUpdate
                      ? (updater) => onCommentsUpdate(form.id, updater)
                      : undefined
                  }
                  disabled={disabled}
                  enableAddComment={enableAddComment}
                  showSubmitButton={false}
                  activeSubformIndex={isActive ? activeSubformIndex : null}
                  onValidationChange={(subformId, validationInfo) =>
                    handleValidationChange(form.id, subformId, validationInfo)
                  }
                  validateOnBlur={!disabled}
                  onStagedFileUpload={onStagedFileUpload}
                  onStagedFileDelete={onStagedFileDelete}
                  eagerUploadEnabled={eagerUploadEnabled}
                  draftStagedFiles={draftStagedFiles}
                  draftDeletedFileIds={draftDeletedFileIds}
                />
              )}
            </SubformNavigation>
          </div>
        );
      })}

      {hasLinkedStudiesSelectorTab &&
        activeFormIndex === tabs.linkedStudiesSelectorIndex && (
          <div className={styles.linkedStudiesContent}>
            {linkedStudiesSelectorSlot}
          </div>
        )}

      {hasLinkedStudiesViewTab &&
        activeFormIndex === tabs.linkedStudiesViewIndex && (
          <div className={styles.linkedStudiesContent}>
            {linkedStudiesViewSlot}
          </div>
        )}

      {hasAdditionalDocumentsTab &&
        activeFormIndex === tabs.additionalDocumentsIndex && (
          <div className={styles.additionalDocumentsContent}>
            {additionalDocumentsSlot}
          </div>
        )}

      {hasRequestAuditTrailsTab &&
        activeFormIndex === tabs.requestAuditTrailsIndex && (
          <div className={styles.requestAuditTrailsContent}>
            {requestAuditTrailsSlot}
          </div>
        )}

      {hasRequestClarificationWavesTab &&
        activeFormIndex === tabs.requestClarificationWavesIndex && (
          <div className={styles.requestClarificationWavesContent}>
            {requestClarificationWavesSlot}
          </div>
        )}
    </div>
  );
};

export default MultiFormView;
